var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import * as React from 'react';
import { Widget_getWidgetSource } from '@leanprover/infoview-api';
import { RpcContext } from './rpcSessions';
import { mapRpcError, useAsync } from './util';
import { ErrorBoundary } from './errors';
function dynamicallyLoadModule(hash, code) {
    return __awaiter(this, void 0, void 0, function () {
        var file, url;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    file = new File([code], "widget_".concat(hash, ".js"), { type: 'text/javascript' });
                    url = URL.createObjectURL(file);
                    return [4 /*yield*/, import(url)];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    });
}
var moduleCache = new Map();
/**
 * Fetch source code from Lean and dynamically import it as a JS module.
 *
 * The source must hash to `hash` (in Lean) and must have been annotated with `@[widget]`
 * or `@[widget_module]` at some point before `pos`. */
export function importWidgetModule(rs, pos, hash) {
    return __awaiter(this, void 0, void 0, function () {
        var resp, mod;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, Widget_getWidgetSource(rs, pos, hash)];
                case 1:
                    resp = _a.sent();
                    if (moduleCache.has(hash)) {
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        return [2 /*return*/, moduleCache.get(hash)];
                    }
                    return [4 /*yield*/, dynamicallyLoadModule(hash, resp.sourcetext)];
                case 2:
                    mod = _a.sent();
                    moduleCache.set(hash, mod);
                    return [2 /*return*/, mod];
            }
        });
    });
}
/**
 * Use {@link importWidgetModule} to import a module which must `export default` a React component,
 * and render that with `props`. Errors in the component are caught in an error boundary. */
export function DynamicComponent(props_) {
    var _this = this;
    var pos = props_.pos, hash = props_.hash, props = props_.props, children = props_.children;
    var rs = React.useContext(RpcContext);
    var component = useAsync(function () { return __awaiter(_this, void 0, void 0, function () {
        var mod;
        var _this = this;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, importWidgetModule(rs, pos, hash)];
                case 1:
                    mod = _a.sent();
                    return [2 /*return*/, React.lazy(function () { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                            return [2 /*return*/, mod];
                        }); }); })];
            }
        });
    }); }, [hash]);
    return (React.createElement(React.Suspense, { fallback: "Loading component ".concat(hash, ": ").concat(component.state, ".") },
        React.createElement(ErrorBoundary, null,
            component.state === 'resolved' && React.createElement(component.value, props, children),
            component.state === 'rejected' && React.createElement(React.Fragment, null,
                "Error: ",
                mapRpcError(component.error).message))));
}
export function PanelWidgetDisplay(_a) {
    var pos = _a.pos, goals = _a.goals, termGoal = _a.termGoal, selectedLocations = _a.selectedLocations, widget = _a.widget;
    var componentProps = __assign({ pos: pos, goals: goals, termGoal: termGoal, selectedLocations: selectedLocations }, widget.props);
    return React.createElement(DynamicComponent, { pos: pos, hash: widget.javascriptHash, props: componentProps });
}
